import React, { useState, useEffect } from 'react'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { fetchWithAuth } from '@/lib/api'
import { getDocumentDisplayIcon } from '@/lib/document-icons'
import {
  displaySegmentsForStoragePath,
  formatUserFacingStoragePath,
} from '@/lib/app-routes'
import { collapseBreadcrumbLabels } from '@/lib/collapse-breadcrumb'
import { withWorkspaceScope } from '@/lib/workspace-scope-query'
import { workspaceForFilePath } from '@/lib/workspace-path-match'
import { workspaceListRowChrome } from '@/components/features/inbox/workspace-accent'
import type { Workspace } from '@/providers/workspace-provider'

import { PersonLink } from '@/components/features/person-link'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import {
  HomeFeedList,
  HomeFeedRow,
  HomeWidgetShell,
} from '@/components/dashboard/home-widget-shell'
import { HomeFeedContextMenu } from '@/components/dashboard/home-feed-context-menu'
import {
  homeSelectionKey,
  type HomeContinuityProps,
  type HomeSelection,
} from '@/components/dashboard/home-selection'
import { testIds } from '@/lib/test-ids'

/** Parent-folder breadcrumb for Recent Activity (workspace label + path, no filename). */
function locationLabelForFilePath(
  filePath: string | undefined,
  workspaces: Workspace[],
  opts?: { collapse?: boolean },
): string | null {
  if (!filePath?.trim()) return null
  const normalized = filePath.replace(/^\/+/, '').replace(/\/+$/, '')
  const slash = normalized.lastIndexOf('/')
  const parent = slash >= 0 ? normalized.slice(0, slash) : ''
  if (!parent) return null
  const segments = displaySegmentsForStoragePath(parent, workspaces)
  const labels =
    segments.length > 0 ? segments.map((s) => s.label) : parent.split('/').filter(Boolean)
  if (labels.length === 0) return null
  if (opts?.collapse === false) return labels.join(' / ')
  return collapseBreadcrumbLabels(labels)
}

interface RecentDocument {
  id: string
  title: string
  type: string
  /** Display clock matching API sort key (edited = updated_at; viewed = last_viewed coalesced). */
  updatedAt: string
  updatedBy?: string
  updatedByUserId?: string | null
  filePath?: string
  lastViewed?: string | null
  lastEdited: string
  /** ISO edit time - prefer for "edited today" style stats. */
  updatedAtRaw?: string
}

interface RecentActivitySectionProps {
  onDocumentClick?: (documentId: string, event?: React.MouseEvent) => void
  activeWorkspaceIds?: string[]
  workspaces?: Workspace[]
  continuity?: HomeContinuityProps
}

function toSelection(doc: RecentDocument): HomeSelection {
  return {
    kind: 'document',
    id: doc.id,
    title: doc.title,
    path: doc.filePath,
  }
}

export function RecentActivitySection({
  onDocumentClick,
  activeWorkspaceIds = [],
  workspaces = [],
  continuity,
}: RecentActivitySectionProps) {
  const [documents, setDocuments] = useState<RecentDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    doc: RecentDocument
  } | null>(null)

  const showWorkspaceRails = continuity?.showWorkspaceRails ?? activeWorkspaceIds.length > 1

  const selectedId = continuity
    ? continuity.selection?.kind === 'document'
      ? continuity.selection.id
      : null
    : localSelectedId

  const fetchRecentDocuments = async () => {
    try {
      setLoading(true)
      setError(null)

      if (activeWorkspaceIds.length === 0) {
        setDocuments([])
        return
      }

      const response = await fetchWithAuth(
        withWorkspaceScope('/api/documents/recent?limit=10&sortBy=edited', activeWorkspaceIds)
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.details || `Failed to fetch recent documents: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.documents) {
        setDocuments(data.documents)
      } else if (Array.isArray(data)) {
        setDocuments(data)
      } else {
        console.warn('[RecentActivitySection] Unexpected response format:', data)
        setDocuments([])
      }
    } catch (err) {
      console.error('[RecentActivitySection] Error fetching recent documents:', err)
      setError(err instanceof Error ? err.message : 'Failed to load recent documents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRecentDocuments()
  }, [activeWorkspaceIds.join(',')])

  useEffect(() => {
    if (!contextMenu) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    const handleClick = () => setContextMenu(null)
    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [contextMenu])

  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'work-spec':
      case 'work_spec':
        return 'Work Spec'
      case 'voice-memo':
      case 'voice_memo':
        return 'Voice Memo'
      case 'quiz':
        return 'Quiz'
      case 'flow':
        return 'Flow'
      case 'note':
        return 'Note'
      default:
        return type
    }
  }

  const selectDoc = (doc: RecentDocument, opts?: { openPreview?: boolean }) => {
    if (continuity) {
      continuity.selectHome(toSelection(doc), opts)
    } else {
      setLocalSelectedId(doc.id)
    }
  }

  const openDoc = (doc: RecentDocument, event?: React.MouseEvent) => {
    if (continuity) {
      continuity.openSelection(toSelection(doc), event)
    } else {
      onDocumentClick?.(doc.id, event)
    }
  }

  const handleDocumentClick = (doc: RecentDocument, event?: React.MouseEvent) => {
    selectDoc(doc)
    if (event?.metaKey || event?.ctrlKey) {
      openDoc(doc, event)
    }
  }

  const handleDocumentDoubleClick = (doc: RecentDocument, event?: React.MouseEvent) => {
    selectDoc(doc)
    openDoc(doc, event)
  }

  return (
    <HomeWidgetShell
      title="Recent Activity"
      data-testid={testIds.home.recentActivity}
      action={
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchRecentDocuments()}
            disabled={loading}
            className="h-7 w-7 p-0"
            title="Refresh recent activity"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </Button>
          {documents.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                window.dispatchEvent(
                  new CustomEvent('flownotes:open-search-view', {
                    detail: { query: '', view: 'activity' },
                  }),
                )
              }}
              className="h-7 px-2"
              title="Full edit timeline for active workspaces"
            >
              All edits
            </Button>
          )}
        </div>
      }
    >
      {contextMenu && (
        <HomeFeedContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpen={() => openDoc(contextMenu.doc)}
          onPreview={() => selectDoc(contextMenu.doc, { openPreview: true })}
          addToChat={{
            id: contextMenu.doc.id,
            title: contextMenu.doc.title,
            path: contextMenu.doc.filePath,
          }}
          filePath={contextMenu.doc.filePath}
          workspaces={continuity?.workspaces ?? workspaces}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="bg-destructive/10 border-destructive/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm font-medium">{error}</AlertDescription>
        </Alert>
      )}

      {!loading && !error && documents.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No recent activity in your active workspaces</p>
          <p className="text-xs mt-1">Open or edit a document here to see it listed</p>
        </div>
      )}

      {!loading && !error && documents.length > 0 && (
        <HomeFeedList>
          {documents.map((doc) => {
            const workspaceColor = workspaceForFilePath(doc.filePath, workspaces)?.color ?? undefined
            const isSelected =
              selectedId === doc.id ||
              (continuity != null &&
                homeSelectionKey(toSelection(doc)) === continuity.selectedKey)
            const rowChrome = workspaceListRowChrome(workspaceColor, isSelected, {
              showRail: showWorkspaceRails,
            })
            const locationFull = locationLabelForFilePath(doc.filePath, workspaces, {
              collapse: false,
            })
            const location = locationLabelForFilePath(doc.filePath, workspaces)
            const typeLabel = getDocumentTypeLabel(doc.type)
            const whenLabel = doc.lastEdited || doc.updatedAt || null
            const pathTooltip = doc.filePath
              ? formatUserFacingStoragePath(doc.filePath, workspaces)
              : locationFull ?? undefined
            const metaNode = (
              <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
                {typeLabel ? <span className="shrink-0">{typeLabel}</span> : null}
                {typeLabel && location ? <span aria-hidden className="shrink-0">·</span> : null}
                {location ? (
                  <span className="min-w-0 truncate" title={pathTooltip}>
                    {location}
                  </span>
                ) : null}
                {doc.updatedBy && doc.updatedBy !== 'Unknown' ? (
                  <>
                    <span aria-hidden className="shrink-0">·</span>
                    <PersonLink
                      name={doc.updatedBy}
                      userId={doc.updatedByUserId}
                      className="hover:text-foreground shrink-0"
                    />
                  </>
                ) : null}
              </span>
            )
            return (
              <HomeFeedRow
                key={doc.id}
                title={doc.title}
                meta={metaNode}
                trailing={whenLabel}
                leading={getDocumentDisplayIcon(
                  { type: doc.type },
                  { className: 'h-4 w-4' },
                )}
                selected={isSelected}
                titleAttr={pathTooltip}
                onClick={(e) => handleDocumentClick(doc, e)}
                onDoubleClick={(e) => handleDocumentDoubleClick(doc, e)}
                onContextMenu={(e) => {
                  e.preventDefault()
                  selectDoc(doc)
                  setContextMenu({ x: e.clientX, y: e.clientY, doc })
                }}
                className={rowChrome.className}
                style={rowChrome.style}
              />
            )
          })}
        </HomeFeedList>
      )}
    </HomeWidgetShell>
  )
}
